import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { useBackNavigation } from "../hooks/useBackNavigation";
import { useBreadcrumb } from "../context/BreadcrumbContext";

import {
  getLPE1ById,
  saveLPE1,
  downloadEditableLpe1Pdf,
  deleteLPE1,
  createLPE1,
  getInitialLPE1Data,
  updateLPE1Meta,
} from "../api/lpe1Api";
import { getApiErrorMessage } from "../utils/concurrency";

import { getPropertyById, getUnitDetails } from "../api/unitApi";

import LPE1Section1, { LPE1MetaPanel } from "./questionnairetemplates/lpe1/LPE1Section1";

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";

const toDateInput = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const getToday = () => toDateInput(new Date());

const addDays = (dateString, days) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + days);
  return toDateInput(date);
};

export default function LPE1Form() {
  const { lpe1Id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const lastPathRef = useRef(`${location.pathname}${location.search}${location.hash}`);
  const goBack = useBackNavigation("/dashboard");
  const { setCustomPath, clearCustomPath } = useBreadcrumb();

  // Check if this is a new form (route is /lpe1/new or lpe1Id is "new" or undefined)
  const isNewForm = location.pathname === "/lpe1/new" || lpe1Id === "new" || !lpe1Id;
  const unitId = useMemo(() => {
    if (!isNewForm) return null;
    return new URLSearchParams(location.search).get("unit_id");
  }, [isNewForm, location.search]);

  const [form, setForm] = useState({});
  const [originalForm, setOriginalForm] = useState({}); // For isDirty comparison
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [hasBeenSaved, setHasBeenSaved] = useState(false); // Track if LPE1 has been saved at least once
  const [currentLpe1Id, setCurrentLpe1Id] = useState(isNewForm ? null : lpe1Id); // Track the actual LPE1 ID
  const [lpe1UnitId, setLpe1UnitId] = useState(null); // Store unit ID from loaded LPE1
  const [rowVersion, setRowVersion] = useState(1);
  const [lpe1Meta, setLpe1Meta] = useState({
    status: "Requested",
    requestedDate: "",
    dueByDate: "",
  });

  // Confirmation dialog for unsaved changes
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);


  // 🔔 toaster state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | info
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  /* -------------------------------
     Load form
  -------------------------------- */
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        
        // Re-check isNewForm inside useEffect to ensure it's current
        const isNew = location.pathname === "/lpe1/new" || lpe1Id === "new" || !lpe1Id;
        
        if (isNew) {
          // New form - get initial form data from property metadata
          const unitIdParam = new URLSearchParams(location.search).get("unit_id");
          const today = getToday();
          const defaultDue = addDays(today, 60);
          setLpe1Meta({
            status: "Requested",
            requestedDate: today,
            dueByDate: defaultDue,
          });
          if (unitIdParam) {
            setLpe1UnitId(parseInt(unitIdParam));
            
            // Fetch initial form data (includes calculated "4.1" value)
            try {
              const unitData = await getUnitDetails(unitIdParam);
              const initialData = await getInitialLPE1Data(parseInt(unitIdParam));
              setForm(initialData);
              setOriginalForm(JSON.parse(JSON.stringify(initialData))); // Deep copy
              if (unitData?.unit?.property_id) {
                const propertyData = await getPropertyById(unitData.unit.property_id);
                setCustomPath([
                  { label: "Properties", path: "/properties" },
                  {
                    label: propertyData?.name || `Property ${unitData.unit.property_id}`,
                    path: `/properties/${unitData.unit.property_id}/details`,
                  },
                  {
                    label:
                      unitData.unit.unit_name ||
                      unitData.unit.unit_code ||
                      `Unit ${unitData.unit.id}`,
                    path: `/units/${unitData.unit.id}/details`,
                  },
                  { label: "LPE1", path: null },
                ]);
              } else {
                setCustomPath([
                  { label: "Properties", path: "/properties" },
                  { label: "New LPE1", path: null },
                ]);
              }
            } catch (err) {
              console.error("Failed to load initial LPE1 data:", err);
              // Fallback to empty form if initial data fails to load
              setForm({});
              setOriginalForm({});
              setCustomPath([
                { label: "Properties", path: "/properties" },
                { label: "New LPE1", path: null },
              ]);
            }
          } else {
            // No unit ID - initialize empty form
            setForm({});
            setOriginalForm({});
            setCustomPath([
              { label: "Properties", path: "/properties" },
              { label: "New LPE1", path: null },
            ]);
          }
          
          setHasBeenSaved(false);
          setLoading(false);
        } else {
          // Existing form - load from database
          if (!lpe1Id || lpe1Id === "new") {
            setError("Invalid LPE1 ID.");
            setLoading(false);
            return;
          }
          const result = await getLPE1ById(lpe1Id);
          const formData = result.formData || {};
          setForm(formData);
          setOriginalForm(JSON.parse(JSON.stringify(formData))); // Deep copy for comparison
          setLpe1Meta({
            status: result.status || "Requested",
            requestedDate: toDateInput(result.requestedDate),
            dueByDate: toDateInput(result.dueByDate),
          });
          setRowVersion(result.rowVersion || 1);

          // Store unit ID for navigation
          if (result.unitId) {
            setLpe1UnitId(result.unitId);
          }

          // If we successfully loaded an existing LPE1 from the database,
          // it has been saved (it exists in the database)
          // Only new forms that haven't been saved yet should have hasBeenSaved = false
          setHasBeenSaved(true);

          // Set breadcrumb path: Properties > PropertyName > UnitName > LPE1
          if (result.propertyId && result.propertyName) {
            const breadcrumbPath = [
              { label: "Properties", path: "/properties" },
              { label: result.propertyName, path: `/properties/${result.propertyId}/details` },
            ];

            // Add unit to path if we have unit info
            if (result.unitId) {
              const unitLabel = result.unitName || result.unitCode || `Unit ${result.unitId}`;
              breadcrumbPath.push({
                label: unitLabel,
                path: `/units/${result.unitId}/details`,
              });
            }

            breadcrumbPath.push({ label: "LPE1", path: null });
            setCustomPath(breadcrumbPath);
          }
        }
      } catch {
        setError("Failed to load LPE1 form.");
      } finally {
        setLoading(false);
      }
    };
    load();

    // Cleanup breadcrumb on unmount
    return () => {
      clearCustomPath();
    };
  }, [lpe1Id, location.pathname, location.search, setCustomPath, clearCustomPath]);

  /* -------------------------------
     Update field
  -------------------------------- */
  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateMeta = (patch) => {
    setLpe1Meta((prev) => ({ ...prev, ...patch }));
  };

  const persistLpe1Meta = async (actualLpe1Id, metaRowVersion = rowVersion) => {
    if (!actualLpe1Id) return;
    const payload = {};
    if (lpe1Meta.status) {
      payload.status = lpe1Meta.status;
    }
    if (lpe1Meta.dueByDate) {
      payload.due_by_date = lpe1Meta.dueByDate;
    }
    if (Object.keys(payload).length === 0) return;

    try {
      // Use version from preceding save when provided — setState is async and
      // would otherwise send a stale row_version and get concurrency_conflict.
      const res = await updateLPE1Meta(actualLpe1Id, payload, metaRowVersion);
      if (res.data?.row_version) {
        setRowVersion(res.data.row_version);
      }
      setLpe1Meta((prev) => ({
        ...prev,
        status: res.data?.status ?? prev.status,
        requestedDate: toDateInput(res.data?.requested_date) || prev.requestedDate,
        dueByDate: toDateInput(res.data?.due_by_date) || prev.dueByDate,
      }));
    } catch {
      showToast("Failed to update LPE1 status", "error");
    }
  };

  /* -------------------------------
     Check if form is dirty (has unsaved changes)
  -------------------------------- */
  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(originalForm),
    [form, originalForm]
  );

  useEffect(() => {
    lastPathRef.current = `${location.pathname}${location.search}${location.hash}`;
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const onClick = (event) => {
      if (!isDirty) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
      const anchor = event.target.closest("a");
      if (!anchor) return;
      if (anchor.getAttribute("target") === "_blank") return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;

      let url;
      try {
        url = new URL(anchor.href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      event.preventDefault();
      setPendingNavigation({
        type: "path",
        path: `${url.pathname}${url.search}${url.hash}`,
      });
      setConfirmDialogOpen(true);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [isDirty]);

  useEffect(() => {
    const onPopState = () => {
      if (!isDirty) return;
      const targetPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const currentPath = lastPathRef.current;
      if (targetPath === currentPath) return;
      setPendingNavigation({ type: "path", path: targetPath });
      setConfirmDialogOpen(true);
      navigate(currentPath, { replace: true });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isDirty, navigate]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  /* =====================================================
     SAVE + OPTIONAL EXPORT
  ===================================================== */
  const attemptSaveWithCheck = async (after) => {
    if (after === "export") {
      setExporting(true);
    }
    try {
      setSaving(true);

      let actualLpe1Id = currentLpe1Id;
      let savedForm = form;
      let latestRowVersion = rowVersion;

      // If this is a new form, create the LPE1 first
      if (isNewForm && !actualLpe1Id) {
        const unitIdToUse = unitId || lpe1UnitId;
        if (!unitIdToUse) {
          showToast("Unit ID is required", "error");
          setSaving(false);
          return;
        }
        const createRes = await createLPE1(unitIdToUse);
        actualLpe1Id = createRes.data?.lpe1_id;
        
        if (!actualLpe1Id) {
          throw new Error("Failed to create LPE1");
        }
        
        // Store unit ID and update the URL and state
        setLpe1UnitId(parseInt(unitIdToUse));
        setCurrentLpe1Id(actualLpe1Id);
        
        // Reload the form data to get the initial values (including 4.1)
        const result = await getLPE1ById(actualLpe1Id);
        const initialFormData = result.formData || {};
        latestRowVersion = result.rowVersion || 1;
        setRowVersion(latestRowVersion);
        
        // Merge initial form data with any user-entered data
        const mergedForm = { ...initialFormData, ...form };
        savedForm = mergedForm;
        setForm(mergedForm);

        // Save first so the subsequent load fetches the latest data
        const saveRes = await saveLPE1(actualLpe1Id, mergedForm, latestRowVersion);
        if (saveRes.data?.row_version) {
          latestRowVersion = saveRes.data.row_version;
          setRowVersion(latestRowVersion);
        }

        navigate(`/lpe1/${actualLpe1Id}`, { replace: true, state: { from: location.state?.from } });
      } else {
        const saveRes = await saveLPE1(actualLpe1Id, form, rowVersion);
        if (saveRes.data?.row_version) {
          latestRowVersion = saveRes.data.row_version;
          setRowVersion(latestRowVersion);
        }
      }
      
      // Mark as saved and update original form
      setHasBeenSaved(true);
      setOriginalForm(JSON.parse(JSON.stringify(savedForm))); // Update original to current
      await persistLpe1Meta(actualLpe1Id, latestRowVersion);

      if (after === "export") {
        await downloadEditableLpe1Pdf(actualLpe1Id);
        showToast("PDF exported");
      } else {
        showToast("LPE1 saved");
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, "Operation failed"), "error");
    } finally {
      setSaving(false);
      setExporting(false);
    }
  };

  const handleSave = () => attemptSaveWithCheck("save");
  const handleExport = () => attemptSaveWithCheck("export");


  /* -------------------------------
     Handle back button click
  -------------------------------- */
  const handleBackClick = () => {
    if (isDirty) {
      // Show confirmation dialog
      setPendingNavigation({ type: "back" });
      setConfirmDialogOpen(true);
    } else {
      // No unsaved changes, proceed with navigation
      handleConfirmNavigation();
    }
  };

  /* -------------------------------
     Navigate back to previous page or default
  -------------------------------- */
  const navigateBack = () => {
    const fromPath = location.state?.from;
    
    // If we came from a property details page and have a unit ID, navigate to unit details
    if (fromPath && fromPath.startsWith("/properties/") && fromPath.endsWith("/details") && lpe1UnitId) {
      navigate(`/units/${lpe1UnitId}/details`);
    } 
    // Otherwise use the from path if available
    else if (fromPath) {
      navigate(fromPath);
    } 
    // Fallback to default
    else {
      goBack();
    }
  };

  /* -------------------------------
     Confirm navigation (after dialog)
  -------------------------------- */
  const handleConfirmNavigation = async () => {
    setConfirmDialogOpen(false);
    
    // If it's a new unsaved LPE1 that was created, delete it
    // (Only delete if we actually created a record - not for pure "new" forms)
    if (!hasBeenSaved && currentLpe1Id) {
      try {
        await deleteLPE1(currentLpe1Id);
      } catch (err) {
        console.error("Failed to delete unsaved LPE1:", err);
        // Continue with navigation even if delete fails
      }
    }
    
    if (pendingNavigation?.type === "path" && pendingNavigation.path) {
      navigate(pendingNavigation.path);
    } else {
      // Navigate back
      navigateBack();
    }
    setPendingNavigation(null);
  };

  /* -------------------------------
     Cancel navigation
  -------------------------------- */
  const handleCancelNavigation = () => {
    setConfirmDialogOpen(false);
    setPendingNavigation(null);
  };

  /* -------------------------------
     Loading
  -------------------------------- */
  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box maxWidth="1000px" mx="auto" px={2} pb={6}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Typography variant="h4">LPE1 Questionnaire</Typography>
            <Typography color="text.secondary">
              {isNewForm ? "New LPE1 Form" : `LPE1 ID: ${lpe1Id}`}
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            <LPE1MetaPanel meta={lpe1Meta} onChange={updateMeta} />

            <Divider />

            <LPE1Section1 data={form} setField={updateField} />

            <Divider />

            <Stack direction="row" justifyContent="space-between">
              <Button variant="outlined" onClick={handleBackClick}>
                {hasBeenSaved ? "Return" : "Cancel"}
              </Button>

              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  Save
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleExport}
                  disabled={saving}
                  startIcon={exporting ? <CircularProgress size={18} /> : null}
                >
                  {exporting ? "Exporting..." : "Export PDF"}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      {/* ===============================
          UNSAVED CHANGES CONFIRMATION DIALOG
      =============================== */}
      <Dialog open={confirmDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>
          {hasBeenSaved ? "Unsaved Changes" : "Discard Changes?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {hasBeenSaved
              ? "You have unsaved changes. Are you sure you want to leave? Any unsaved data will be lost."
              : "You have unsaved changes. If you cancel, this LPE1 will be deleted. Are you sure you want to continue?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation}>Stay</Button>
          <Button
            variant="contained"
            color={hasBeenSaved ? "primary" : "error"}
            onClick={handleConfirmNavigation}
          >
            {hasBeenSaved ? "Leave" : "Cancel & Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===============================
          TOAST / SNACKBAR
      =============================== */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
